import React, { useState, useEffect, useMemo } from 'react';
import { useSocket } from '../../context/SocketContext';
import { apiService } from '../../services/api';
import { delegationService } from '../../services/delegationService';
import { ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { canUserVote } from '../../context/AuthContext';
import { notifyError } from '../../utils/notificationCenter';

interface VotingParticipationProps {
  assemblyId: string;
  userRole: 'admin' | 'manager' | 'owner' | 'tenant' | 'guest';
}

const shellCardClassName = 'rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_-20px_rgba(15,23,42,0.45)]';

const VotingParticipation: React.FC<VotingParticipationProps> = ({
  assemblyId,
  userRole
}) => {
  const { activeVotingSession, activeSessions: socketActiveSessions, isConnected } = useSocket();
  const [selectedOptions, setSelectedOptions] = useState<{[sessionId: string]: string}>({});
  const [votedSessions, setVotedSessions] = useState<Set<string>>(new Set());
  const [submittingSessions, setSubmittingSessions] = useState<Set<string>>(new Set());
  const [activeSessions, setActiveSessions] = useState<any[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [canVoteByDelegation, setCanVoteByDelegation] = useState<boolean>(true);
  const [hiddenSessionIds, setHiddenSessionIds] = useState<Set<string>>(new Set());
  const [isRefreshingSessions, setIsRefreshingSessions] = useState(false);

  // Cargar información del usuario actual
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await apiService.getUserProfile();
        if (response.data.success) {
          setCurrentUser(response.data.user);
        }
      } catch (error) {
        console.error('Error al cargar usuario:', error);
      }
    };

    loadCurrentUser();
  }, []);

  // Verificar si el usuario puede votar (delegaciones)
  useEffect(() => {
    const loadVotingWeight = async () => {
      try {
        const response = await delegationService.getVotingWeight(assemblyId);
        setCanVoteByDelegation(response.canVote);
      } catch (error) {
        console.error('Error al cargar peso de voto:', error);
        setCanVoteByDelegation(true);
      }
    };

    if (assemblyId) {
      loadVotingWeight();
    }
  }, [assemblyId]);

  // ✅ OPTIMIZACIÓN: Cargar sesiones activas SOLO una vez al inicio
  // Las actualizaciones en tiempo real vienen por Socket.IO a través de socketActiveSessions
  const loadActiveSessions = async () => {
    try {
      console.log('📥 Carga de sesiones activas para assemblyId:', assemblyId);
      const response = await apiService.getVotingSessionsLiveView(assemblyId);
      const sessionsData = response.data.data || response.data.sessions || response.data;

      // Buscar todas las sesiones activas (excluyendo CANCELLED)
      const activeSessionsList = Array.isArray(sessionsData)
        ? sessionsData.filter((session: any) => session.status === 'active')
        : [];

      console.log('✅ Sesiones activas cargadas:', activeSessionsList.length);
      setActiveSessions(activeSessionsList);
    } catch (error) {
      console.error('❌ Error al cargar sesiones activas:', error);
      notifyError('No se pudieron refrescar las votaciones activas.');
    }
  };

  useEffect(() => {
    if (assemblyId) {
      loadActiveSessions();
    }
  }, [assemblyId]); // ✅ Solo se ejecuta cuando cambia assemblyId

  // ✅ OPTIMIZACIÓN: Sincronizar con las sesiones del SocketContext en tiempo real
  useEffect(() => {
    if (socketActiveSessions.length > 0) {
      console.log('🔄 Actualizando sesiones desde SocketContext:', socketActiveSessions.length, 'sesiones');
      
      // Combinar sesiones: mantener las locales y agregar/actualizar las del socket
      setActiveSessions(prev => {
        const combined = [...prev];
        
        socketActiveSessions.forEach(socketSession => {
          const index = combined.findIndex(s => s.id === socketSession.id);
          if (index >= 0) {
            // Actualizar sesión existente
            combined[index] = socketSession;
          } else {
            // Agregar nueva sesión
            combined.push(socketSession);
          }
        });
        
        return combined;
      });
    }
  }, [socketActiveSessions]); // ✅ Reacciona a cambios en la lista del SocketContext

  // ✅ OPTIMIZACIÓN: Eventos personalizados solo para casos específicos (cancelación/eliminación)
  // Los eventos de inicio vienen por Socket.IO directamente a través de activeVotingSession
  useEffect(() => {
    // Listener para evento personalizado de votación cancelada
    const handleVotingSessionCancelled = (event: any) => {
      console.log('🚫 Evento de votación cancelada/eliminada recibido:', event.detail);
      if (event.detail?.assemblyId === assemblyId) {
        console.log('🔄 Removiendo sesión cancelada de la lista');
        setActiveSessions(prev => prev.filter(s => s.id !== event.detail.sessionId));
      }
    };

    // Registrar listeners de eventos personalizados
    window.addEventListener('voting-session-cancelled', handleVotingSessionCancelled);
    window.addEventListener('voting-session-deleted', handleVotingSessionCancelled);

    // ✅ OPTIMIZACIÓN: Eliminado el polling (setInterval) - ahora usamos Socket.IO para actualizaciones en tiempo real
    // Las actualizaciones vienen a través de activeVotingSession del SocketContext

    return () => {
      window.removeEventListener('voting-session-cancelled', handleVotingSessionCancelled);
      window.removeEventListener('voting-session-deleted', handleVotingSessionCancelled);
    };
  }, [assemblyId]);

  // ✅ OPTIMIZACIÓN: No necesitamos combinar con useMemo, activeVotingSession ya está en activeSessions
  // El useEffect anterior ya se encarga de agregar/actualizar activeVotingSession en la lista
  const allActiveSessions = activeSessions;

  const handleRefreshSessions = async () => {
    if (!assemblyId || isRefreshingSessions) {
      return;
    }

    setIsRefreshingSessions(true);
    try {
      await loadActiveSessions();
    } finally {
      setIsRefreshingSessions(false);
    }
  };

  const canClearVoted = useMemo(() => {
    return allActiveSessions.some(
      session => votedSessions.has(session.id) && !hiddenSessionIds.has(session.id)
    );
  }, [allActiveSessions, votedSessions, hiddenSessionIds]);

  const visibleSessions = useMemo(() => {
    return allActiveSessions.filter(session => !hiddenSessionIds.has(session.id));
  }, [allActiveSessions, hiddenSessionIds]);

  // Consultar si el usuario ya votó en cada sesión activa
  useEffect(() => {
    const checkUserVotes = async () => {
      if (allActiveSessions.length === 0) {
        setVotedSessions(new Set());
        return;
      }

      try {
        const sessionIds = allActiveSessions.map((session: any) => session.id);
        const response = await apiService.getUserVotedStatusBatch(sessionIds);
        const statusBySession = response.data?.data || {};
        const votedSet = new Set<string>(
          Object.entries(statusBySession)
            .filter(([, hasVoted]) => Boolean(hasVoted))
            .map(([sessionId]) => sessionId)
        );
        setVotedSessions(votedSet);
      } catch (error) {
        console.error('Error al consultar votos en batch:', error);
      }
    };

    checkUserVotes();
  }, [allActiveSessions]);

  const handleVoteSubmit = async (sessionId: string) => {
    const mockUser = { role: userRole } as any;
    const selectedOption = selectedOptions[sessionId];
    
    if (
      !selectedOption ||
      votedSessions.has(sessionId) ||
      !canUserVote(mockUser) ||
      !canVoteByDelegation
    ) {
      return;
    }

    setSubmittingSessions(prev => {
      const newSet = new Set(prev);
      newSet.add(sessionId);
      return newSet;
    });
    
    try {
      if (!currentUser?.id) {
        notifyError('Error: No se pudo obtener la información del usuario.');
        return;
      }

      const voteData = {
        votingSessionId: sessionId,
        userId: currentUser.id,
        vote: selectedOption
      };

      const response = await apiService.castVote(voteData);
      console.log('Voto enviado exitosamente:', response.data);

      setVotedSessions(prev => {
        const newSet = new Set(prev);
        newSet.add(sessionId);
        return newSet;
      });
      setSelectedOptions(prev => ({ ...prev, [sessionId]: '' }));
      
    } catch (error) {
      console.error('Error al enviar voto:', error);
      notifyError('Error al enviar el voto. Por favor, inténtalo de nuevo.');
    } finally {
      setSubmittingSessions(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  if (!isConnected) {
    return (
      <div className={`${shellCardClassName} border-yellow-200 bg-yellow-50 p-4`}>
        <div className="flex items-center">
          <ClockIcon className="h-5 w-5 text-yellow-400 mr-2" />
          <span className="text-yellow-800">Conectando al servidor...</span>
        </div>
      </div>
    );
  }

  if (allActiveSessions.length === 0) {
    return (
      <div className={`${shellCardClassName} bg-slate-50 p-6`}>
        <div className="text-center">
          <ClockIcon className="mx-auto mb-4 h-12 w-12 text-slate-400" />
          <h3 className="mb-2 text-lg font-medium text-slate-900">
            No hay votaciones activas
          </h3>
          <p className="text-slate-600">
            No hay sesiones de votación en curso para esta asamblea.
          </p>
        </div>
      </div>
    );
  }

  const mockUser = { role: userRole } as any;
  if (!canUserVote(mockUser) || !canVoteByDelegation) {
    return (
      <div className={`${shellCardClassName} border-blue-200 bg-blue-50 p-6`}>
        <div className="text-center">
          <h3 className="mb-2 text-lg font-medium text-blue-900">
            Modo Observador
          </h3>
          <p className="text-blue-700">
            {!canVoteByDelegation
              ? 'Has delegado tu voto, por lo que no puedes participar en esta votación.'
              : 'Puedes observar las votaciones pero no participar en ellas.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefreshSessions}
            disabled={isRefreshingSessions}
            className="inline-flex items-center rounded-lg border border-blue-300 px-3 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isRefreshingSessions ? 'animate-spin' : ''}`} />
            {isRefreshingSessions ? 'Refrescando...' : 'Refrescar'}
          </button>
          <button
            type="button"
            onClick={() => {
              if (!canClearVoted) return;
              setHiddenSessionIds(prev => {
                const next = new Set(prev);
                allActiveSessions.forEach(session => {
                  if (votedSessions.has(session.id)) {
                    next.add(session.id);
                  }
                });
                return next;
              });
            }}
            disabled={!canClearVoted}
            className={`self-start rounded-lg border px-3 py-1 text-xs font-medium transition-colors sm:self-auto ${
              canClearVoted
                ? 'border-slate-300 text-slate-700 hover:bg-slate-50'
                : 'cursor-not-allowed border-slate-200 text-slate-400'
            }`}
          >
            Limpiar votadas
          </button>
        </div>
      </div>

      {visibleSessions.length === 0 ? (
        <div className={`${shellCardClassName} bg-slate-50 p-6`}>
          <div className="text-center">
            <ClockIcon className="mx-auto mb-4 h-12 w-12 text-slate-400" />
            <h3 className="mb-2 text-lg font-medium text-slate-900">
              No hay votaciones activas
            </h3>
            <p className="text-slate-600">
              No hay sesiones de votación en curso para esta asamblea.
            </p>
          </div>
        </div>
      ) : (
        visibleSessions.map((session: any) => {
          const hasVotedInSession = votedSessions.has(session.id);
          const selectedOptionForSession = selectedOptions[session.id];
          const isSubmittingSession = submittingSessions.has(session.id);

          return (
          <div key={session.id} className={shellCardClassName}>
            <div className="border-b border-slate-200 bg-gradient-to-r from-blue-50/70 to-transparent p-4 md:p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-slate-900">
                    {session.question || session.title}
                  </h3>
                  <p className="mt-1 text-xs md:text-sm text-slate-600">
                    {session.description}
                  </p>
                </div>
              </div>

              {hasVotedInSession && (
                <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-3 md:p-4">
                  <div className="flex items-center">
                    <span className="text-xs md:text-sm text-green-800 font-medium">Tu voto ha sido registrado para esta sesión</span>
                  </div>
                </div>
              )}

            </div>

            <div className="p-4 md:p-6">
              {!hasVotedInSession && (
                <div className="space-y-3 mb-6">
                  {session.options.map((option: any) => {
                    const canVote = !hasVotedInSession && canUserVote(mockUser) && canVoteByDelegation;
                    return (
                      <div
                        key={option.id}
                        className={`cursor-pointer rounded-xl border p-3 transition-colors md:p-4 ${
                          selectedOptionForSession === option.id
                            ? 'border-blue-500 bg-blue-50 shadow-sm'
                            : canVote
                            ? 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                            : 'cursor-not-allowed border-slate-200 bg-slate-50'
                        }`}
                        onClick={() => {
                          if (canVote) {
                            setSelectedOptions(prev => ({
                              ...prev,
                              [session.id]: option.id
                            }));
                          }
                        }}
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name={`vote-option-${session.id}`}
                            value={option.id}
                            checked={selectedOptionForSession === option.id}
                            onChange={() => {
                              setSelectedOptions(prev => ({
                                ...prev,
                                [session.id]: option.id
                              }));
                            }}
                            disabled={!canVote}
                            className="h-4 w-4 flex-shrink-0 border-slate-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                          />
                          <label className="ml-3 text-sm font-medium text-slate-900 md:text-base">
                            {option.text}
                          </label>
                        </div>
                        {option.description && (
                          <p className="mt-2 ml-7 text-sm text-slate-600">
                            {option.description}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {!hasVotedInSession && canUserVote(mockUser) && canVoteByDelegation && (
                <div className="flex justify-end">
                  <button
                    onClick={() => handleVoteSubmit(session.id)}
                    disabled={!selectedOptionForSession || isSubmittingSession}
                    className={`w-full rounded-xl px-4 py-2 text-sm font-medium transition-all md:px-6 md:py-3 md:text-base sm:w-auto ${
                      selectedOptionForSession && !isSubmittingSession
                        ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
                        : 'cursor-not-allowed bg-slate-300 text-slate-500'
                    }`}
                  >
                    {isSubmittingSession ? 'Enviando...' : 'Enviar Voto'}
                  </button>
                </div>
              )}
            </div>
          </div>
          );
        })
      )}
    </div>
  );
};

export default VotingParticipation;
